import { Popover } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { LuSettings2 } from 'react-icons/lu';
import {
  gameStakes,
  gameType,
  StakeName,
  stakesOptions,
  StakesOptionsBet,
  tableSize,
} from '../../../features/lobby/constants/filter-options';
import Checkbox from '../../../components/new-design/Checkbox';
import { FC, useEffect } from 'react';
import Button, { ButtonSizes, ButtonVariants } from '../../../components/new-design/Button';
import { TableFilter } from '../../../services/lobby/lobbyServicesTypes';
import { GameValue } from '../../../features/lobby/filters/filters';
import { BettingStructure } from '../../../services/common/types/bettingStructure';

const transformStakeOptions = (
  stakeOptions: StakesOptionsBet[],
  stakeOptionsFilter: TableFilter['stakeOptions'],
): StakesOptionsBet[] => {
  const transformedOptions: StakesOptionsBet[] = [];

  if (!stakeOptionsFilter) return [];

  stakeOptionsFilter.forEach((filterOption) => {
    stakeOptions.forEach((option) => {
      option.stakes.forEach((stake) => {
        if (
          stake.range.min === filterOption.min &&
          stake.range.max === filterOption.max &&
          option.bettingStructure === filterOption.bettingStructure
        ) {
          transformedOptions.push({
            ...option,
            stakes: [stake],
          });
        }
      });
    });
  });

  return transformedOptions;
};

interface PopoverMenuProps {
  filters: TableFilter;
  handleRangeChange: (minValue: number, maxValue: number) => void;
  handleTableSizeChange: (size: number) => void;
  isMobileView?: boolean;
  handleEmptyTables?: () => void;
  handleFullTables?: () => void;
  handleFavoritesOnly?: () => void;
  handleStakes?: (gameStake: StakeName) => void;
  handleGameType?: (gameType: BettingStructure) => void;
  handleGames?: (game: GameValue) => void;
  handleResetFilters: () => void;
  handleSaveFilters: () => void;
  handleCloseWithoutSaving: () => void;
}

const PopoverMenu: FC<PopoverMenuProps> = ({
  filters,
  handleRangeChange,
  handleTableSizeChange,
  isMobileView = false,
  handleEmptyTables = () => {},
  handleFullTables = () => {},
  handleFavoritesOnly = () => {},
  handleStakes = () => {},
  handleGameType = () => {},
  handleGames = () => {},
  handleResetFilters = () => {},
  handleSaveFilters = () => {},
  handleCloseWithoutSaving = () => {},
}) => {
  let numberOfFilters = 0;
  if (filters.games?.length) numberOfFilters++;
  if (filters.maxPlayers?.length) numberOfFilters++;
  if (filters.stakeOptions?.length) numberOfFilters++;
  if (filters.gameTypes?.length) numberOfFilters++;
  if (filters.fullTables) numberOfFilters++;
  if (filters.emptyTables) numberOfFilters++;

  useEffect(() => {
    const targetNode = document.querySelector('[data-headlessui-state]');
    if (!targetNode) return;

    const observerCallback = (mutationsList: any) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-headlessui-state') {
          if (mutation.target.getAttribute('data-headlessui-state') !== 'open') handleCloseWithoutSaving();
        }
      }
    };

    const observer = new MutationObserver(observerCallback);

    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ['data-headlessui-state'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Popover.Button
        className={twMerge(
          isMobileView
            ? 'flex h-11 w-11 items-center justify-center rounded-lg bg-ph-primary-violet text-sm font-bold text-white'
            : 'flex h-11 select-none items-center gap-2 rounded-lg bg-ph-violet-light px-7 font-manrope text-sm font-bold tracking-wide text-ph-gray-silver hover:bg-ph-gray-baltic',
        )}
      >
        <LuSettings2 size={18} />
        {!isMobileView && <span className="text-ph-gray-silver">Filters</span>}

        {!!numberOfFilters && !isMobileView && (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ph-primary-violet p-1 text-ph-gray-white">
            {numberOfFilters}
          </span>
        )}
      </Popover.Button>

      <Popover.Panel className={'absolute right-0 z-50 mt-3 w-screen max-w-sm transform'}>
        {({ close }) => (
          <div className="relative">
            <div
              className={twMerge(
                'absolute right-0 rounded-xl bg-phenom-border-glow p-[1px]',
                isMobileView && 'w-[95vw]',
              )}
            >
              <div
                className={twMerge(
                  'flex h-full w-full gap-10 rounded-xl bg-pn-violet-medium p-10',
                  isMobileView && 'flex-col',
                )}
              >
                <div className="flex flex-col gap-5">
                  <div className={twMerge(isMobileView ? 'flex flex-col gap-4' : 'flex flex-row gap-10')}>
                    <div className={twMerge(isMobileView ? 'grid grid-cols-1 gap-y-10' : 'flex flex-row gap-10')}>
                      <div className={twMerge('flex flex-col gap-10', isMobileView && 'flex-row gap-3')}>
                        <div className={twMerge('flex w-48 flex-col gap-3', isMobileView && 'min-w-[8.2em]')}>
                          <span className="mb-2 font-manrope text-sm font-bold tracking-wide text-ph-gray-jumbo">
                            Game Types
                          </span>

                          {gameType.map((gameType) => {
                            const isSelected = filters.gameTypes?.includes(gameType);
                            return (
                              <Checkbox
                                key={gameType}
                                checked={!!isSelected}
                                onChange={() => handleGameType(gameType)}
                                text={gameType}
                              />
                            );
                          })}
                        </div>

                        <div className={twMerge('flex w-48 flex-col gap-3', isMobileView && 'min-w-60')}>
                          <span className="mb-2 font-manrope text-sm font-bold tracking-wide text-ph-gray-jumbo">
                            Game Stakes
                          </span>

                          {gameStakes.map((stake) => {
                            const isSelected = transformStakeOptions(stakesOptions, filters.stakeOptions).some(
                              (option) =>
                                option.stakes.some((stakeOption) => {
                                  return stakeOption.stakeName === stake;
                                }),
                            );

                            return (
                              <Checkbox
                                key={stake}
                                checked={isSelected}
                                onChange={() => handleStakes(stake)}
                                text={stake}
                              />
                            );
                          })}
                        </div>
                      </div>

                      <div className={twMerge('flex flex-row gap-10', isMobileView && 'gap-3')}>
                        <div className="mr-10 flex flex-col gap-3">
                          <span className="mb-2 whitespace-nowrap font-manrope text-sm font-bold tracking-wide text-ph-gray-jumbo">
                            Table Size
                          </span>

                          {tableSize.map((tableSize) => (
                            <Checkbox
                              key={tableSize}
                              checked={!!filters?.maxPlayers?.includes(tableSize)}
                              onChange={() => handleTableSizeChange(tableSize)}
                              text={
                                tableSize === 2
                                  ? isMobileView
                                    ? `Heads Up`
                                    : `Display Heads Up Tables`
                                  : `${tableSize} Players`
                              }
                            />
                          ))}
                        </div>

                        <div className="mr-10 flex flex-col gap-3">
                          <span className="mb-2 whitespace-nowrap font-manrope text-sm font-bold tracking-wide text-ph-gray-jumbo">
                            Preferences
                          </span>

                          {/* <Checkbox
                            checked={false}
                            onChange={() => {}}
                            text="Favorites Only"
                            className="hover:cursor-not-allowed"
                          /> */}

                          <Checkbox
                            checked={!!filters.emptyTables}
                            onChange={handleEmptyTables}
                            text="Hide empty tables"
                          />

                          <Checkbox
                            checked={!!filters.fullTables}
                            onChange={handleFullTables}
                            text="Hide full tables"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="-mx-10 mb-2 mt-6 border-ph-gray-evening" />

                  <div className={twMerge('flex justify-end gap-5', isMobileView && 'flex flex-col')}>
                    <Button
                      type="button"
                      variant={ButtonVariants.transparent}
                      className="whitespace-nowrap p-2"
                      size={ButtonSizes.sm}
                      onClick={handleResetFilters}
                    >
                      Reset Filters
                    </Button>

                    <div
                      className={twMerge(
                        'flex w-full items-center justify-end gap-5',
                        isMobileView && 'justify-center',
                      )}
                    >
                      <Button
                        type="button"
                        className={twMerge('bg-pn-violet-medium', isMobileView ? 'w-1/2' : 'min-w-32')}
                        variant={ButtonVariants.secondary}
                        onClick={() => {
                          handleCloseWithoutSaving();
                          close();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        className={twMerge(isMobileView ? 'w-1/2' : 'min-w-32')}
                        onClick={() => {
                          handleSaveFilters();
                          close();
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Popover.Panel>
    </>
  );
};

export default PopoverMenu;
